import { Divider, FocusTrap, Text } from "@mantine/core";
import { useMemo } from "react";

import {
  AssignUserSelector,
  NOT_ASSIGNED,
  useAssignUserSingleSelectHelper,
} from "@/components/assign-user-selector";
import { useBulkAssignUserToLocationsMutation } from "@/hooks/queries/screener-locations.queries";
import type { ScreenerLocation } from "@/types/screener-location";
import { TestID } from "@/utils/helpers";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";
import { ModalPosition, ModalSizes, TableModalWrapper } from ".";

const MODAL_CONFIG = {
  position: ModalPosition.BottomRight,
  size: ModalSizes.ExtraSmall,
  withOverlay: false,
  withCloseButton: false,
} as const;

interface LocationAssignmentModalProps {
  opened: boolean;
  close: () => void;
  selectedRows: ScreenerLocation[];
}

export const LocationAssignmentModal = ({
  opened,
  close,
  selectedRows,
}: LocationAssignmentModalProps) => {
  const { onAssignedToMeView } = useAssignUserSingleSelectHelper();
  const { mutate: bulkAssignUser, isPending: mutationPending } =
    useBulkAssignUserToLocationsMutation();

  const selectedUserId = useMemo(() => {
    const uniqueLocationAssignments = new Set(
      selectedRows.map(({ assignedTo }) => assignedTo?.id).filter(Boolean),
    );

    if (uniqueLocationAssignments.size === 1) {
      return uniqueLocationAssignments.values().next().value;
    }
  }, [selectedRows]);

  const handleSubmit = (userId: string) => {
    const locationIds = selectedRows.map((row) => row.id);
    const currentAffectedCount = locationIds.length;

    bulkAssignUser(
      {
        locationIds,
        userId: userId === NOT_ASSIGNED ? null : userId,
      },
      {
        onSuccess: () => {
          close();
          showUpwageNotification(
            `${currentAffectedCount} locations assigned`,
            NotificationTypes.Success,
            {
              autoClose: 3000,
            },
          );
        },
      },
    );
  };

  return (
    <TableModalWrapper opened={opened} {...MODAL_CONFIG}>
      <FocusTrap.InitialFocus />
      <div className="flex justify-center items-center py-4">
        <Text size="sm">{selectedRows.length} location(s) selected</Text>
      </div>
      <Divider />
      <div className="flex flex-col gap-2 py-4">
        <Text size="sm" fw={500}>
          Assign to
        </Text>
        <AssignUserSelector
          baseTestId={
            new TestID(
              onAssignedToMeView ? "my-locations" : "screener-locations",
              "bulk-assign-modal",
            )
          }
          fullWidth
          keyPrefix="bulk-assign-modal"
          loading={mutationPending}
          onChange={handleSubmit}
          value={selectedUserId ? [selectedUserId] : []}
        />
      </div>
    </TableModalWrapper>
  );
};
